/**
 * Widget tag-filter form helpers (issue #489)
 *
 * Converts between the persisted `DashboardWidgetFilter` shape (a flat
 * `WidgetTagFilter[]` + combining `operator`) and the flat form state a config UI
 * naturally edits (separate include-tags / include-categories / exclude-tags
 * lists plus a combiner). Kept as pure functions so the conversion is
 * independently testable from the editing component.
 */

import type {
  DashboardWidgetFilter,
  WidgetTagFilter,
  TagFilterCombiner,
} from '../types/preferences';

export interface WidgetTagFilterFormState {
  includeTagIds: string[];
  includeCategoryIds: string[];
  excludeTagIds: string[];
  combiner: TagFilterCombiner;
}

export const EMPTY_WIDGET_TAG_FILTER_FORM: WidgetTagFilterFormState = {
  includeTagIds: [],
  includeCategoryIds: [],
  excludeTagIds: [],
  combiner: 'AND',
};

/** Parses a persisted `DashboardWidgetFilter` into editable form state. */
export function parseDashboardWidgetFilter(
  filters: DashboardWidgetFilter | undefined
): WidgetTagFilterFormState {
  if (!filters) {
    return EMPTY_WIDGET_TAG_FILTER_FORM;
  }

  const includeTagIds: string[] = [];
  const includeCategoryIds: string[] = [];
  const excludeTagIds: string[] = [];

  for (const filter of filters.tagFilters) {
    if (filter.operator === 'include' && filter.type === 'tag') {
      includeTagIds.push(filter.id);
    } else if (filter.operator === 'include' && filter.type === 'category') {
      includeCategoryIds.push(filter.id);
    } else if (filter.operator === 'exclude' && filter.type === 'tag') {
      excludeTagIds.push(filter.id);
    }
    // Exclude-by-category is not supported by the editor (issue #489 scope);
    // any such filter present in persisted data is preserved via round-trip
    // callers that merge rather than replace, but is not surfaced here.
  }

  return { includeTagIds, includeCategoryIds, excludeTagIds, combiner: filters.operator };
}

/**
 * Builds a `DashboardWidgetFilter` from form state, or `undefined` when the
 * form is empty (no filter configured — the widget shows unfiltered data).
 */
export function buildDashboardWidgetFilter(
  form: WidgetTagFilterFormState
): DashboardWidgetFilter | undefined {
  const tagFilters: WidgetTagFilter[] = [
    ...form.includeTagIds.map((id): WidgetTagFilter => ({ type: 'tag', id, operator: 'include' })),
    ...form.includeCategoryIds.map((id): WidgetTagFilter => ({
      type: 'category',
      id,
      operator: 'include',
    })),
    ...form.excludeTagIds.map((id): WidgetTagFilter => ({ type: 'tag', id, operator: 'exclude' })),
  ];

  if (tagFilters.length === 0) {
    return undefined;
  }

  return { filterType: 'tags', tagFilters, operator: form.combiner };
}
